import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { COLOR, FONT, RADIUS, SPACE, Z } from "../lib/tokens";
import { usePhoneDirectory } from "../lib/usePhoneDirectory";
import { shareInvite, copyInviteLink, inviteUrlFor } from "../lib/shareInvite";

// FindFriendsSheet — phone-contact directory flow.
//
// One sheet, three states:
//   1. CTA — explain what happens, ask for contact permission
//   2. Results — matched profiles with "FAMILY" / "FRIEND" actions
//   3. Empty — "no one in your contacts is on Booda yet"
//
// All hashing is client-side (see phoneHash.js). The matched profile
// rows come back from match_phone_hashes RPC (migration 0176).
//
// Props:
//   open
//   onClose
//   userId
//   invite        — useRelationships.invite(code, kind)  — already used by
//                   the existing invite-code paste path
//   existingIds   — Set<user_id> of relationships already in place; matches
//                   already connected get rendered as "ALREADY ADDED"

export default function FindFriendsSheet({
  open,
  onClose,
  userId,
  invite,
  existingIds,
  // When true, the sheet auto-runs the contact scan the moment it
  // opens — used by the end-of-onboarding hand-off so the user lands
  // with matches surfaced instead of staring at an "intro" CTA.
  autoStart = false,
  // The viewer's own invite code + name — powers the share-invite
  // fallback when contacts match nobody (or on web, where contacts
  // don't exist at all).
  inviteCode = null,
  viewerName = "",
}) {
  const dir = usePhoneDirectory(userId);
  const [phase, setPhase] = useState("intro"); // intro | results | empty | invite
  const [matches, setMatches] = useState([]);
  const [inviting, setInviting] = useState(null);   // user_id of in-flight invite
  const [invited, setInvited] = useState(new Set()); // user_ids successfully invited this session
  const [copied, setCopied] = useState(false);

  const [tab, setTab] = useState("contacts"); // 'contacts' | 'username'
  const [usernameQuery, setUsernameQuery] = useState("");

  // Zero contact matches → the SHARE fallback, not a dead-end
  // paragraph pointing at Settings. (Username-search misses keep the
  // plain "empty" phase — different situation, different copy.)
  const emptyTarget = inviteCode ? "invite" : "empty";

  // Re-seed on open so a re-open after a permission grant kicks fresh.
  useEffect(() => {
    if (!open) return;
    setPhase("intro");
    setMatches([]);
    setInvited(new Set());
    setTab("contacts");
    setUsernameQuery("");
    setCopied(false);
    if (autoStart) {
      // Web has no contacts API at all — usePhoneDirectory errors on
      // non-native. Route the onboarding hand-off straight to the
      // share-invite mode instead of a guaranteed failure.
      if (!dir.nativeSupported) {
        setPhase(inviteCode ? "invite" : "intro");
        return;
      }
      // Defer one tick so the sheet's "intro" frame renders before we
      // pop the iOS contact-permission alert — without that beat the
      // permission sheet stacks on top of a blank backdrop.
      const t = setTimeout(() => {
        (async () => {
          const found = await dir.findFromContacts();
          setMatches(found);
          setPhase(found.length === 0 ? emptyTarget : "results");
        })();
      }, 80);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStart]);

  if (!open) return null;

  const start = async () => {
    const found = await dir.findFromContacts();
    setMatches(found);
    setPhase(found.length === 0 ? emptyTarget : "results");
  };

  const doShare = async () => {
    const { copied: didCopy } = await shareInvite({ inviteCode, name: viewerName });
    if (didCopy) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const doCopy = async () => {
    if (await copyInviteLink(inviteCode)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const searchUsername = async (e) => {
    e?.preventDefault?.();
    if (!usernameQuery.trim()) return;
    const match = await dir.findByUsername(usernameQuery);
    setMatches(match ? [match] : []);
    setPhase(match ? "results" : "empty");
  };

  const handleInvite = async (row, kind) => {
    if (!row?.invite_code || !invite) return;
    setInviting(row.user_id);
    try {
      await invite(row.invite_code, kind);
      setInvited((prev) => {
        const next = new Set(prev);
        next.add(row.user_id);
        return next;
      });
    } catch (e) {
      console.warn("[findFriends] invite failed:", e?.message || e);
    } finally {
      setInviting(null);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,20,20,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: Z.sheet,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: COLOR.night,
          borderTopLeftRadius: RADIUS.sheet,
          borderTopRightRadius: RADIUS.sheet,
          padding: `${SPACE.section}px ${SPACE.section}px calc(${SPACE.hero}px + env(safe-area-inset-bottom))`,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.2,
          color: COLOR.muted,
          marginBottom: SPACE.xs,
        }}>
          FIND FRIENDS
        </div>
        <div style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 22,
          color: COLOR.ink,
          marginBottom: SPACE.md,
        }}>
          {tab === "username" ? "By username" : "From your contacts"}
        </div>

        {/* Tab strip */}
        {phase === "intro" && (
          <div style={{
            display: "flex", gap: 0, padding: 3,
            background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
            borderRadius: 10, marginBottom: SPACE.section,
          }}>
            <button
              type="button"
              onClick={() => { setTab("contacts"); setMatches([]); }}
              style={tabStyle(tab === "contacts")}
            >
              CONTACTS
            </button>
            <button
              type="button"
              onClick={() => { setTab("username"); setMatches([]); }}
              style={tabStyle(tab === "username")}
            >
              @ USERNAME
            </button>
          </div>
        )}

        {phase === "intro" && tab === "contacts" && (
          <>
            <div style={{
              fontFamily: FONT.sans,
              fontSize: 13,
              color: COLOR.ink,
              lineHeight: 1.55,
              marginBottom: SPACE.section,
            }}>
              Booda will check your phone's contacts for anyone already on the
              app. Phone numbers stay on your device — we only send hashed
              fingerprints used to match. You can opt out anytime by clearing
              your phone in Settings.
            </div>
            {!dir.nativeSupported && (
              <div style={{
                fontFamily: FONT.mono,
                fontSize: 11,
                letterSpacing: 0.6,
                color: COLOR.rose,
                padding: `${SPACE.sm}px ${SPACE.md}px`,
                background: COLOR.roseDeep,
                border: `1px solid ${COLOR.roseDim}`,
                borderRadius: RADIUS.md,
                marginBottom: SPACE.section,
              }}>
                Contact sync is iOS-only in this version. On web, use the
                invite-code paste flow.
              </div>
            )}
            <div style={{ display: "flex", gap: SPACE.md }}>
              <button
                type="button"
                className="mcm-focusable"
                onClick={onClose}
                style={cancelBtn}
              >
                CANCEL
              </button>
              <button
                type="button"
                className="mcm-focusable"
                onClick={start}
                disabled={dir.busy || !dir.nativeSupported}
                style={{
                  ...primaryBtn,
                  opacity: dir.busy ? 0.6 : 1,
                  background: !dir.nativeSupported ? COLOR.deep : COLOR.leaf,
                  color: !dir.nativeSupported ? COLOR.muted : "#fff",
                }}
              >
                {dir.busy ? "CHECKING…" : "CHECK MY CONTACTS"}
              </button>
            </div>
            {dir.error && (
              <div style={{ ...errorStyle, marginTop: SPACE.md }}>
                {dir.error}
              </div>
            )}
          </>
        )}

        {phase === "intro" && tab === "username" && (
          <>
            <div style={{
              fontFamily: FONT.sans,
              fontSize: 13,
              color: COLOR.ink,
              lineHeight: 1.55,
              marginBottom: SPACE.section,
            }}>
              If your friend told you their Booda handle, drop it in here. Lowercase letters, numbers, or underscores.
            </div>
            <form onSubmit={searchUsername} style={{ display: "flex", gap: SPACE.md, marginBottom: SPACE.section }}>
              <div style={{
                flex: 1, display: "flex", alignItems: "center",
                padding: `0 ${SPACE.md}px`, borderRadius: RADIUS.md,
                background: COLOR.ground, border: `1px solid ${COLOR.border}`,
              }}>
                <span style={{ fontFamily: FONT.mono, fontSize: 16, color: COLOR.muted }}>@</span>
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={usernameQuery}
                  onChange={(e) => setUsernameQuery(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24))}
                  placeholder="theirhandle"
                  style={{
                    flex: 1, padding: `${SPACE.md}px ${SPACE.xs}px`,
                    border: "none", background: "transparent",
                    color: COLOR.ink, fontFamily: FONT.mono, fontSize: 15,
                    outline: "none", letterSpacing: "0.02em",
                  }}
                />
              </div>
              <button
                type="submit"
                className="mcm-focusable"
                disabled={!usernameQuery.trim() || dir.busy}
                style={{
                  ...primaryBtn,
                  flex: "0 0 auto",
                  padding: `0 ${SPACE.section}px`,
                  background: usernameQuery.trim() ? COLOR.leaf : COLOR.deep,
                  color: usernameQuery.trim() ? "#fff" : COLOR.muted,
                  opacity: dir.busy ? 0.6 : 1,
                }}
              >
                {dir.busy ? "…" : "FIND"}
              </button>
            </form>
            <button
              type="button"
              className="mcm-focusable"
              onClick={onClose}
              style={{ ...cancelBtn, width: "100%" }}
            >
              CANCEL
            </button>
            {dir.error && (
              <div style={{ ...errorStyle, marginTop: SPACE.md }}>
                {dir.error}
              </div>
            )}
          </>
        )}

        {phase === "results" && (
          <>
            <div style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: 1.2,
              color: COLOR.muted,
              marginBottom: SPACE.sm,
            }}>
              {matches.length} ON BOODA
            </div>
            <ul style={{
              listStyle: "none",
              padding: 0,
              margin: `0 0 ${SPACE.section}px`,
              display: "flex",
              flexDirection: "column",
              gap: SPACE.xs,
            }}>
              {matches.map((row) => {
                const already = existingIds?.has(row.user_id);
                const justInvited = invited.has(row.user_id);
                const inFlight = inviting === row.user_id;
                return (
                  <li key={row.user_id} style={resultCardStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: SPACE.md }}>
                      {row.avatar_url ? (
                        <img
                          src={row.avatar_url}
                          alt=""
                          style={avatarStyle}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div style={{
                          ...avatarStyle,
                          background: COLOR.deep,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: FONT.mono,
                          fontSize: 14,
                          color: COLOR.muted,
                        }}>
                          {(row.name || "?")[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: FONT.forma,
                          fontSize: 14,
                          fontWeight: 600,
                          color: COLOR.ink,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {row.name || "Booda user"}
                        </div>
                        {row.contactLabel && row.contactLabel !== row.name && (
                          <div style={{
                            fontFamily: FONT.mono,
                            fontSize: 9,
                            letterSpacing: 0.8,
                            color: COLOR.muted,
                            marginTop: 2,
                          }}>
                            {row.contactLabel.toUpperCase()} · IN CONTACTS
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: SPACE.sm, display: "flex", gap: SPACE.xs }}>
                      {already || justInvited ? (
                        <span style={{
                          ...pillStyle,
                          background: COLOR.deep,
                          color: COLOR.muted,
                        }}>
                          {already ? "ALREADY ADDED" : "INVITED"}
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="mcm-focusable"
                            disabled={inFlight}
                            onClick={() => handleInvite(row, "family")}
                            style={{
                              ...pillBtn,
                              background: COLOR.leaf,
                              color: "#fff",
                              opacity: inFlight ? 0.6 : 1,
                            }}
                          >
                            + FAMILY
                          </button>
                          <button
                            type="button"
                            className="mcm-focusable"
                            disabled={inFlight}
                            onClick={() => handleInvite(row, "friend")}
                            style={{
                              ...pillBtn,
                              background: COLOR.ground,
                              color: COLOR.ink,
                              border: `1px solid ${COLOR.border}`,
                              opacity: inFlight ? 0.6 : 1,
                            }}
                          >
                            + FRIEND
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="mcm-focusable"
              onClick={onClose}
              style={{ ...primaryBtn, background: COLOR.ground, color: COLOR.ink, border: `1px solid ${COLOR.border}`, width: "100%" }}
            >
              DONE
            </button>
          </>
        )}

        {phase === "empty" && (
          <>
            <div style={{
              fontFamily: FONT.sans,
              fontSize: 13,
              color: COLOR.muted,
              lineHeight: 1.55,
              marginBottom: SPACE.section,
            }}>
              No match found. Check the spelling, or share your invite link
              so they can join you.
            </div>
            {inviteCode && (
              <button
                type="button"
                className="mcm-focusable"
                onClick={() => setPhase("invite")}
                style={{ ...primaryBtn, background: COLOR.leaf, color: "#fff", width: "100%", marginBottom: 8 }}
              >
                SHARE MY INVITE LINK
              </button>
            )}
            <button
              type="button"
              className="mcm-focusable"
              onClick={onClose}
              style={{ ...primaryBtn, background: COLOR.ground, color: COLOR.ink, border: `1px solid ${COLOR.border}`, width: "100%" }}
            >
              DONE
            </button>
          </>
        )}

        {phase === "invite" && (
          <>
            <div style={{
              fontFamily: FONT.sans,
              fontSize: 13,
              color: COLOR.ink,
              lineHeight: 1.55,
              marginBottom: SPACE.md,
            }}>
              {dir.nativeSupported
                ? "No one in your contacts is on Booda yet — be the first. Anyone who taps your link lands in your kitchen as family or friend."
                : "Invite your family and friends — anyone who taps your link lands in your kitchen."}
            </div>
            <div style={{
              padding: "12px 14px", marginBottom: SPACE.md,
              background: COLOR.soil, border: `1px solid ${COLOR.border}`,
              borderRadius: RADIUS.md,
              fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {inviteUrlFor(inviteCode)}
            </div>
            <button
              type="button"
              className="mcm-focusable"
              onClick={doShare}
              style={{ ...primaryBtn, background: COLOR.leaf, color: "#fff", width: "100%", marginBottom: 8 }}
            >
              SHARE INVITE LINK
            </button>
            <button
              type="button"
              className="mcm-focusable"
              onClick={doCopy}
              style={{ ...primaryBtn, background: COLOR.ground, color: copied ? COLOR.leaf : COLOR.ink, border: `1px solid ${COLOR.border}`, width: "100%", marginBottom: 8 }}
            >
              {copied ? "COPIED" : "COPY LINK"}
            </button>
            <button
              type="button"
              className="mcm-focusable"
              onClick={() => { setTab("username"); setPhase("intro"); }}
              style={{ ...primaryBtn, background: "transparent", color: COLOR.muted, border: "none", width: "100%", marginBottom: 4, fontSize: 12 }}
            >
              OR SEARCH BY @USERNAME
            </button>
            <button
              type="button"
              className="mcm-focusable"
              onClick={onClose}
              style={{ ...primaryBtn, background: COLOR.ground, color: COLOR.ink, border: `1px solid ${COLOR.border}`, width: "100%" }}
            >
              DONE
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

const cancelBtn = {
  flex: 1,
  fontFamily: FONT.mono,
  fontSize: 11,
  letterSpacing: 1.4,
  padding: `${SPACE.md}px 0`,
  background: COLOR.ground,
  color: COLOR.ink,
  border: `1px solid ${COLOR.border}`,
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

const primaryBtn = {
  flex: 2,
  fontFamily: FONT.mono,
  fontSize: 11,
  letterSpacing: 1.4,
  padding: `${SPACE.md}px 0`,
  background: COLOR.leaf,
  color: "#fff",
  border: "none",
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

const resultCardStyle = {
  padding: `${SPACE.md}px ${SPACE.lg}px`,
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  borderRadius: RADIUS.md,
};

const avatarStyle = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  objectFit: "cover",
  flex: "0 0 auto",
};

const pillStyle = {
  fontFamily: FONT.mono,
  fontSize: 10,
  letterSpacing: 1.2,
  padding: "5px 10px",
  borderRadius: 999,
};

const pillBtn = {
  ...pillStyle,
  border: "none",
  cursor: "pointer",
};

const errorStyle = {
  fontFamily: FONT.mono,
  fontSize: 11,
  color: COLOR.rose,
  padding: `${SPACE.xs}px ${SPACE.sm}px`,
  background: COLOR.roseDeep,
  border: `1px solid ${COLOR.roseDim}`,
  borderRadius: RADIUS.sm,
};

const tabStyle = (active) => ({
  flex: 1,
  padding: `${SPACE.sm}px 0`,
  background: active ? COLOR.edge : "transparent",
  border: "none",
  borderRadius: 7,
  fontFamily: FONT.mono,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.08em",
  color: active ? COLOR.ink : COLOR.muted,
  cursor: "pointer",
});
